import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import DictionaryApiService from "../../system/integrate/DictionaryApiService";
import Logger from "../../../services/Logger";

export default function BannedWordMeaningDialog({ open, onClose, word }) {
    const { language } = useAdminLanguage();
    const [wordData, setWordData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Format category name for display (e.g., "high_frequency" -> "High Frequency")
    const formatCategoryLabel = (category) => {
        if (!category) return 'N/A';
        return category
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useEffect(() => {
        const fetchWordDefinition = async () => {
            if (!open || !word?.word) {
                setWordData(null);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const result = await DictionaryApiService.getWordDefinition(word.word, word.language || language);
                if (result && result.length > 0) {
                    setWordData(result[0]);
                } else {
                    setWordData(null);
                }
            } catch (err) {
                Logger.error("Error fetching word definition:", err);
                setError("Failed to fetch word definition");
                setWordData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchWordDefinition();
    }, [open, word, language]);

    const handleClose = () => {
        setWordData(null);
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }}>
                Word: {word?.word}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Category
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        {formatCategoryLabel(word?.category)}
                    </Typography>

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={40} />
                        </Box>
                    )}

                    {error && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {!loading && !error && wordData && wordData.meanings && wordData.meanings.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
                                Definition
                            </Typography>
                            {wordData.meanings.map((meaning, index) => (
                                <Box key={index} sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, textTransform: 'capitalize' }}>
                                        {meaning.partOfSpeech}
                                    </Typography>
                                    {meaning.definitions.map((def, defIndex) => (
                                        <Box key={defIndex} sx={{ mb: 1, pl: 2 }}>
                                            <Typography variant="body1" sx={{ mb: 0.5 }}>
                                                {def.definition}
                                            </Typography>
                                            {def.example && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                                                    Example: {def.example}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </>
                    )}

                    {!loading && !error && (!wordData || !wordData.meanings || wordData.meanings.length === 0) && (
                        <Typography variant="body2" color="text.secondary">
                            No definition available for this word
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

